import { inject, Injectable, Signal } from '@angular/core';
import { get, isUndefined, last } from 'lodash-es';
import { UserService } from './user.service';
import { RVestiging } from '@cumlaude/service-contract';
import { dutchDate, getBeginjaar, isBooleanAttribute, isDateAttribute, SchooljaarHistorieFilterValue } from '@cumlaude/shared-utils';
import { formatDecimal } from '@cumlaude/shared-pipes';
import { toSignal } from '@angular/core/rxjs-interop';
import { nonBreakingSpace } from './labels';
import { EnumService, LegendaEnumNames } from '../core/services/enum.service';
import { Attr, AttrPath } from '@cumlaude/metadata';

type AttrMap = {
	[attr in Attr]?: AttrMap | string;
};

const nullValues: AttrMap = {
	aw_fun_nr_maanden_instroom_na_1okt: 'Geen instroom',
	aw_fun_nm_instroommoment: 'Geen instroom',
	aw_fun_nm_nationaliteit: 'Niet gevuld',
	aw_fun_nm_abs_reden: 'Aanwezig',

	bv_fun_nr_maanden_instroom_na_1okt: 'Geen instroom',
	bv_fun_nm_instroommoment: 'Geen instroom',
	bv_fun_nm_nationaliteit: 'Niet gevuld',

	cf_fun_nr_maanden_instroom_na_1okt: 'Geen instroom',
	cf_fun_nm_instroommoment: 'Geen instroom',
	cf_fun_nm_nationaliteit: 'Niet gevuld',
	cf_nm_bijzonderheid: 'Geen',
	cf_nm_eerder_geexamineerd: 'Niet van toepassing',

	ds_fun_nr_maanden_instroom_na_1okt: 'Geen instroom',
	ds_fun_nm_instroommoment: 'Geen instroom',
	ds_fun_nm_nationaliteit: 'Niet gevuld',
	ds_nm_instroom_in_schooljaar: 'Geen instroom',
	ds_nm_instroom_van: 'Geen instroom',
	ds_nm_uitstroom_in_schooljaar: 'Geen uitstroom',
	ds_nm_uitstroom: 'Geen uitstroom',
	ds_nm_uitstroommoment_in_schooljaar: 'Geen',
	ds_nm_uitstroomtype_in_schooljaar: 'Geen',

	ilt_abb_profiel: 'Geen',
	ilt_nm_profiel: 'Geen',

	lb_nm_instroom_in_schooljaar: 'Geen',
	lb_nm_instroom: 'Geen',
	lb_nm_instroomtype_in_schooljaar: 'Geen',
	lb_nm_instroomtype: 'Geen',
	lb_nm_uitstroom_in_schooljaar: 'Geen',
	lb_nm_uitstroom: 'Geen',
	lb_nm_uitstroommoment_in_schooljaar: 'Geen',
	lb_nm_uitstroommoment: 'Geen',
	lb_nm_uitstroomtype_in_schooljaar: 'Geen',
	lb_nm_uitstroomtype: 'Geen',
	lb_nm_vorige_vestiging: 'Extern',

	ll_des_uitschrijving: 'Niet van toepassing',
	ll_nm_basisschooladvies: 'Onbekend/Niet gevuld',
	ll_nm_basisschooladvies_definitief: 'Onbekend/Niet gevuld',
	ll_nm_basisschooladvies_duo: 'Onbekend/Niet gevuld',
	ll_nm_basisschooladvies_herzien: 'Onbekend/Niet gevuld',
	ll_nm_basisschooladvies_oud: 'Onbekend/Niet gevuld',
	ll_nm_basisschooladvies_voorlopig: 'Onbekend/Niet gevuld',
	ll_nm_nationaliteit1: 'Niet gevuld',
	ll_nm_nationaliteit2: 'Niet gevuld',

	lr_fun_nr_maanden_instroom_na_1okt: 'Geen instroom',
	lr_fun_nm_instroommoment: 'Geen instroom',
	lr_fun_nm_nationaliteit: 'Niet gevuld',

	vkk_fun_nr_maanden_instroom_na_1okt: 'Geen instroom',
	vkk_fun_nm_instroommoment: 'Geen instroom',
	vkk_fun_nm_nationaliteit: 'Niet gevuld',
};

export const NULLVALUE_LABEL = 'Onbekend';

@Injectable({
	providedIn: 'root',
})
export class DisplayService {
	private readonly vestigingen: Signal<RVestiging[]>;

	constructor() {
		const userService = inject(UserService);
		this.vestigingen = toSignal(userService.rVestigingen$, { initialValue: [] });
	}

	displayForBoolean(value: boolean | number | null | undefined, attr: AttrPath | [LegendaEnumNames]) {
		if (isUndefined(value)) return '';
		if (value == null) return this.displayNullValue(attr);

		return EnumService.booleanEnumEntry(attr, Boolean(value));
	}

	display(dbValue: any | null | undefined, attr?: AttrPath): string {
		if (isUndefined(dbValue)) return '';
		if (dbValue === null) return this.displayNullValue(attr);

		return this.displayFromDbValue(dbValue, attr);
	}

	displayIndicatorSchooljaar(value: string | null | undefined, yearOffset: number): string {
		if (value === undefined) return '';
		if (value === null) return NULLVALUE_LABEL;
		return `${getBeginjaar(value) + yearOffset} (${value})`;
	}

	displaySchooljaarHistorie(value: SchooljaarHistorieFilterValue | null | undefined): string {
		if (value === undefined) return '';
		if (value === null) return NULLVALUE_LABEL;
		return `${value.option}`;
	}

	displayPeriode(value: string | null | undefined): string {
		if (value === undefined) return '';
		if (value === null) return NULLVALUE_LABEL;
		return `Periode ${value}`;
	}

	displayNullValue(attr: AttrPath | [LegendaEnumNames] | undefined) {
		if (!attr) return NULLVALUE_LABEL;

		for (let i = 0; i < attr.length; i++) {
			const v = get(nullValues, attr.slice(i));
			if (v) return v;
		}

		return NULLVALUE_LABEL;
	}

	private displayFromDbValue(dbValue: any, attr?: AttrPath) {
		if (attr && isDateAttribute(attr)) return dutchDate(dbValue);
		if (attr && isBooleanAttribute(attr)) return EnumService.booleanEnumEntry(attr, Boolean(Number(dbValue)));
		if (attr?.join('.') == 'ekc_fk_br_vest.br_co_brin') return this.generateBrinVestigingValueString(dbValue);
		if (typeof dbValue === 'number' && !last(attr)?.endsWith('_nr_leerling')) return formatDecimal(dbValue);

		return `${dbValue}`;
	}

	describe(dbValue: any, attr: AttrPath) {
		if (last(attr)?.startsWith('ilt_nm_niveau')) return dbValue === null ? 'Niveau onbekend' : this.display(dbValue, attr);
		if (last(attr)?.startsWith('ds_nr_leerjaar'))
			return dbValue === null ? `lj${nonBreakingSpace}onbekend` : 'lj' + nonBreakingSpace + this.display(dbValue, attr);
		if (last(attr)?.startsWith('ds_nm_idu')) return dbValue === null ? 'IDU onbekend' : this.display(dbValue, attr);
		return this.display(dbValue, attr);
	}

	generateBrinVestigingValueString(brinvest: string | null) {
		if (brinvest == null) return NULLVALUE_LABEL;

		const vestigingNamen = this.vestigingen()
			.filter((vestiging) => vestiging.actief)
			.filter((vestiging) => vestiging.vestigingBrinsActief.includes(brinvest))
			.map((vestiging) => vestiging.naam)
			.filter((value, index, array) => array.indexOf(value) === index);
		const namen = vestigingNamen.length > 0 ? ` (${vestigingNamen.join(', ')})` : '';
		return `${brinvest}${namen}`;
	}
}
